import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, filter, take } from 'rxjs';

/**
 * Guards outbound API requests during a concurrent-session key reset.
 *
 * When resetSessionKey() is about to fire DELETE /session, this gate is
 * closed (ready = false). The CustomHeadersInterceptor waits for the gate
 * to open before forwarding any request to the backend. Once DELETE /session
 * returns and the stale Redis key is cleared, the gate opens (ready = true)
 * and all buffered requests proceed.
 *
 * This prevents dashboard API calls from racing ahead of DELETE /session
 * and hitting the concurrent-session conflict before the stale key is gone.
 */
@Injectable({ providedIn: 'root' })
export class SessionGateService {
  private ready$ = new BehaviorSubject<boolean>(true);

  /** Close the gate — all non-session API calls will wait. */
  beginReset(): void {
    this.ready$.next(false);
  }

  /** Open the gate — all buffered calls proceed. */
  completeReset(): void {
    this.ready$.next(true);
  }

  /**
   * Returns an Observable that emits once when the gate is open.
   * Completes immediately if the gate is already open.
   */
  waitForReady(): Observable<boolean> {
    return this.ready$.pipe(
      filter(ready => ready),
      take(1)
    );
  }
}
